/**
 * ScenarioFilters Component
 * Filter controls for evaluation scenarios with tag multi-select
 */

import React from 'react';
import { Select } from '@/components/ui/Select';
import { Search, Filter, X } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { AgentTypeOption } from '@/types/evalScenarios';

interface ScenarioFiltersProps {
  filters: {
    agent_type: string;
    category: string;
    source: string;
    is_active: string;
    search: string;
    tags: string[];
  };
  onFilterChange: (filters: Partial<ScenarioFiltersProps['filters']>) => void;
  categories: string[];
  agentTypes?: AgentTypeOption[];
  availableTags?: string[];
}

export const ScenarioFilters: React.FC<ScenarioFiltersProps> = ({
  filters,
  onFilterChange,
  categories,
  agentTypes = [],
  availableTags = [],
}) => {
  const { messages } = useI18n();
  const t = messages.evalScenarios;

  const agentTypeOptions = [
    { value: '', label: t.allAgentTypes },
    ...agentTypes.map((a) => ({ value: a.value, label: a.label })),
  ];

  const categoriesArray = Array.isArray(categories) ? categories : [];
  const categoryOptions = [
    { value: '', label: t.allCategories },
    ...categoriesArray.map((cat) => ({ value: cat, label: cat })),
  ];

  const sourceOptions = [
    { value: '', label: t.allSources },
    { value: 'manual', label: t.sourceManual },
    { value: 'chat_promoted', label: t.sourceChatPromoted },
  ];

  const statusOptions = [
    { value: '', label: t.allStatus },
    { value: 'true', label: t.statusActive },
    { value: 'false', label: t.statusInactive },
  ];

  // Tags not yet selected
  const unselectedTags = availableTags.filter((tag) => !filters.tags.includes(tag));

  const handleAddTag = (tag: string) => {
    if (tag && !filters.tags.includes(tag)) {
      onFilterChange({ tags: [...filters.tags, tag] });
    }
  };

  const handleRemoveTag = (tag: string) => {
    onFilterChange({ tags: filters.tags.filter((t) => t !== tag) });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-center gap-2 text-gray-700">
        <Filter className="w-4 h-4" />
        <span className="font-medium">{t.filters}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={filters.search}
            onChange={(e) => onFilterChange({ search: e.target.value })}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Agent Type */}
        <Select
          options={agentTypeOptions}
          value={filters.agent_type}
          onChange={(e) => onFilterChange({ agent_type: e.target.value })}
        />

        {/* Category */}
        <Select
          options={categoryOptions}
          value={filters.category}
          onChange={(e) => onFilterChange({ category: e.target.value })}
        />

        {/* Source */}
        <Select
          options={sourceOptions}
          value={filters.source}
          onChange={(e) => onFilterChange({ source: e.target.value })}
        />

        {/* Status */}
        <Select
          options={statusOptions}
          value={filters.is_active}
          onChange={(e) => onFilterChange({ is_active: e.target.value })}
        />
      </div>

      {/* Tag Filter Row */}
      {availableTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500 shrink-0">{t.filterByTags || 'Tags'}:</span>

          {/* Selected tags as chips */}
          {filters.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700"
            >
              {tag}
              <button
                onClick={() => handleRemoveTag(tag)}
                className="hover:text-violet-900 ml-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}

          {/* Add tag dropdown */}
          {unselectedTags.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                handleAddTag(e.target.value);
                e.target.value = '';
              }}
              className="px-2 py-1 border border-gray-300 rounded-lg text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
            >
              <option value="">+ {t.addTagFilter || 'Add tag'}</option>
              {unselectedTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          )}

          {/* Clear all tags */}
          {filters.tags.length > 0 && (
            <button
              onClick={() => onFilterChange({ tags: [] })}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              {t.clearTags || 'Clear'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
